import * as React from "react";

import {
  Async,
  BaseComponent,
  divProperties,
  EventGroup,
  getNativeProps,
  memoizeFunction,
  shallowCompare,
} from "@fluentui/react/lib/Utilities";

import { AppContext } from "@1js/acui-context";

import type { AppResizeGroupProps } from "./AppResizeGroup.Props";
import { AppResizeGroupDirection } from "./AppResizeGroup.Props";

const RESIZE_DELAY = 16;

export interface AppResizeGroupState {
  /**
   * Final data used to render proper sized component
   */
  renderedData?: any;

  /**
   * Data to render in a hidden div for measurement
   */
  dataToMeasure?: any;

  /**
   * Previous props to detect changes in props so that we can reset state.
   */
  prevProps?: AppResizeGroupProps;

  /**
   * Set to true when the content container might have new dimensions and should
   * be remeasured.
   */
  measureContainer?: boolean;

  /**
   * Are we resizing to accommodate having more or less available space?
   * The 'grow' direction is when the container may have more room than the last render,
   * such as when a window resize occurs. This means we will try to fit more content in the window.
   * The 'shrink' direction is when the contents don't fit in the container and we need
   * to find a transformation of the data that makes everything fit.
   */
  resizeDirection?: "grow" | "shrink";
}

/**
 * Returns a simple object is able to store measurements with a given key.
 */
export const getMeasurementCache = () => {
  const measurementsCache: { [key: string]: number } = {};

  return {
    /**
     * Checks if the provided data has a cacheKey. If it has a cacheKey and there is a
     * corresponding entry in the measurementsCache, then it will return that value.
     * Returns undefined otherwise.
     */
    getCachedMeasurement: (data: any): number | undefined => {
      if (
        data &&
        data.cacheKey &&
        measurementsCache.hasOwnProperty(data.cacheKey)
      ) {
        return measurementsCache[data.cacheKey];
      }
      /* If the data has provided an estimatedWidth, use it as if it were a cached measurement */
      return data?.estimatedWidth;
    },
    /**
     * Should be called whenever there is a new measurement associated with a given data object.
     * If the data has a cacheKey, store that measurement in the measurementsCache.
     */
    addMeasurementToCache: (data: any, measurement: number): void => {
      if (data.cacheKey) {
        measurementsCache[data.cacheKey] = measurement;
      }
    },
  };
};

/**
 * Returns a function that is able to compute the next state for the ResizeGroup given the current
 * state and any measurement updates.
 */
export const getNextResizeGroupStateProvider = (
  measurementCache = getMeasurementCache()
) => {
  const _measurementCache = measurementCache;
  let _containerDimension: number | undefined;

  /**
   * Gets the width/height of the data rendered in a hidden div.
   * @param measuredData - The data corresponding to the measurement we wish to take.
   * @param getElementToMeasureDimension - A function that returns the measurement of the rendered data. Only called when the measurement
   * is not in the cache.
   * @param ignoreCachedMeasurement - If true, the cached value will be ignored and the DOM measurement will always be used
   */
  function getMeasuredDimension(
    measuredData: any,
    getElementToMeasureDimension: () => number,
    ignoreCachedMeasurement?: boolean
  ): number {
    const cachedDimension =
      _measurementCache.getCachedMeasurement(measuredData);
    if (cachedDimension !== undefined && !ignoreCachedMeasurement) {
      return cachedDimension;
    }

    const measuredDimension = getElementToMeasureDimension();
    _measurementCache.addMeasurementToCache(measuredData, measuredDimension);
    return measuredDimension;
  }

  /**
   * Will get the next AppResizeGroupState based on the current data while trying to shrink contents
   * to fit in the container.
   * @param data - The initial data point to start measuring.
   * @param onReduceData - Function that transforms the data into something that should render with less width/height.
   * @param getElementToMeasureDimension - A function that returns the measurement of the rendered data. Only called when the measurement
   * is not in the cache.
   */
  function shrinkContentsUntilTheyFit(
    data: any,
    onReduceData: (prevData: any, measuredDimension?: number) => any,
    getElementToMeasureDimension: () => number,
    isScalingComplete?: (data: any) => boolean,
    dataNeedsMeasuring?: boolean
  ): AppResizeGroupState {
    let dataToMeasure = data;
    let measuredDimension: number | undefined = getMeasuredDimension(
      data,
      getElementToMeasureDimension,
      dataNeedsMeasuring
    );

    while (
      measuredDimension > _containerDimension! ||
      (isScalingComplete && !isScalingComplete(dataToMeasure))
    ) {
      const nextMeasuredData = onReduceData(dataToMeasure, measuredDimension);

      // We don't want to get stuck in an infinite render loop when there are no more
      // scaling steps, so implementations of onReduceData should return undefined when
      // there are no more scaling states to apply.
      if (nextMeasuredData === undefined) {
        return {
          renderedData: dataToMeasure,
          resizeDirection: undefined,
          dataToMeasure: undefined,
        };
      }

      const contentsFitContainer = !(measuredDimension > _containerDimension!);
      measuredDimension =
        _measurementCache.getCachedMeasurement(nextMeasuredData);

      // isScalingComplete depends on the content existing in the DOM. If we simply pull the measurement from cache, this dependency is broken
      // If contentsFitContainer is true, then we must still be in this loop because isScalingComplete is returning false and will need to be re-calculated on the next iteration.
      // If the measurement isn't in the cache or the above is true, we need to rerender with some data in a hidden div
      if (
        measuredDimension === undefined ||
        (dataNeedsMeasuring && contentsFitContainer)
      ) {
        return {
          dataToMeasure: nextMeasuredData,
          resizeDirection: "shrink",
        };
      }

      dataToMeasure = nextMeasuredData;
    }

    return {
      renderedData: dataToMeasure,
      resizeDirection: undefined,
      dataToMeasure: undefined,
    };
  }

  /**
   * This function should be called when the state changes in a manner that might allow for more content to fit
   * on the screen, such as the window width/height growing.
   * @param data - The initial data point to start measuring.
   * @param onGrowData - Function that transforms the data into something that may take up more space when rendering.
   * @param getElementToMeasureDimension - A function that returns the measurement of the rendered data. Only called when the measurement
   * is not in the cache.
   */
  function growDataUntilItDoesNotFit(
    data: any,
    onGrowData: (prevData: any) => any,
    getElementToMeasureDimension: () => number,
    onReduceData: (prevData: any, measuredDimension?: number) => any,
    isScalingComplete?: (data: any) => boolean,
    dataNeedsMeasuring?: boolean
  ): AppResizeGroupState {
    let dataToMeasure = data;
    let measuredDimension: number | undefined = getMeasuredDimension(
      data,
      getElementToMeasureDimension,
      dataNeedsMeasuring
    );

    while (measuredDimension < _containerDimension!) {
      const nextMeasuredData = onGrowData(dataToMeasure);

      // We don't want to get stuck in an infinite render loop when there are no more
      // scaling steps, so implementations of onGrowData should return undefined when
      // there are no more scaling states to apply.
      if (nextMeasuredData === undefined) {
        if (isScalingComplete && !isScalingComplete(data)) {
          break;
        }
        return {
          renderedData: dataToMeasure,
          resizeDirection: undefined,
          dataToMeasure: undefined,
        };
      }

      measuredDimension =
        _measurementCache.getCachedMeasurement(nextMeasuredData);
      // If the measurement isn't in the cache, we need to rerender with some data in a hidden div
      if (measuredDimension === undefined) {
        return {
          dataToMeasure: nextMeasuredData,
        };
      }

      dataToMeasure = nextMeasuredData;
    }

    // Once the loop is done, we should now shrink until the contents fit.
    return {
      resizeDirection: "shrink",
      ...shrinkContentsUntilTheyFit(
        dataToMeasure,
        onReduceData,
        getElementToMeasureDimension,
        isScalingComplete
      ),
    };
  }

  /**
   * Handles an update to the container width/eheight. Should only be called when we knew the previous container width/height.
   * @param newDimension - The new width/height of the container.
   * @param fullDimensionData - The initial data passed in as a prop to resizeGroup.
   * @param renderedData - The data that was rendered prior to the container size changing.
   * @param onGrowData - Set to true if the Resize group has an onGrowData function.
   */
  function updateContainerDimension(
    newDimension: number,
    fullDimensionData: any,
    renderedData: any,
    onGrowData?: (prevData: any) => any
  ): AppResizeGroupState {
    let nextState: AppResizeGroupState;
    if (newDimension === _containerDimension!) {
      nextState = {};
    } else if (newDimension > _containerDimension!) {
      if (onGrowData) {
        nextState = {
          resizeDirection: "grow",
          dataToMeasure: onGrowData(renderedData),
        };
      } else {
        nextState = {
          resizeDirection: "shrink",
          dataToMeasure: fullDimensionData,
        };
      }
    } else {
      nextState = {
        resizeDirection: "shrink",
        dataToMeasure: renderedData,
      };
    }
    _containerDimension = newDimension;
    return { ...nextState, measureContainer: false };
  }

  function getNextState(
    props: AppResizeGroupProps,
    currentState: AppResizeGroupState,
    getElementToMeasureDimension: () => number,
    newContainerDimension?: number
  ): AppResizeGroupState | undefined {
    // If there is no new container width/height or data to measure, there is no need for a new state update
    if (
      newContainerDimension === undefined &&
      currentState.dataToMeasure === undefined
    ) {
      return undefined;
    }

    if (newContainerDimension) {
      // If we know what the last container size was and we rendered data at that width/height, we can do an optimized render
      if (
        _containerDimension &&
        currentState.renderedData &&
        !currentState.dataToMeasure
      ) {
        return {
          ...currentState,
          ...updateContainerDimension(
            newContainerDimension,
            props.data,
            currentState.renderedData,
            props.onGrowData
          ),
        };
      }

      // If we are just setting the container width/height for the first time, we can't do any optimizations
      _containerDimension = newContainerDimension;
    }

    let nextState: AppResizeGroupState = {
      ...currentState,
      measureContainer: false,
    };

    if (currentState.dataToMeasure) {
      if (currentState.resizeDirection === "grow" && props.onGrowData) {
        nextState = {
          ...nextState,
          ...growDataUntilItDoesNotFit(
            currentState.dataToMeasure,
            props.onGrowData,
            getElementToMeasureDimension,
            props.onReduceData,
            props.isScalingComplete,
            props.dataNeedsMeasuring
          ),
        };
      } else {
        nextState = {
          ...nextState,
          ...shrinkContentsUntilTheyFit(
            currentState.dataToMeasure,
            props.onReduceData,
            getElementToMeasureDimension,
            props.isScalingComplete,
            props.dataNeedsMeasuring
          ),
        };
      }
    }

    return nextState;
  }

  /** Function that determines if we need to render content for measurement based on the measurement cache contents. */
  function shouldRenderDataForMeasurement(
    dataToMeasure: any | undefined
  ): boolean {
    if (
      !dataToMeasure ||
      _measurementCache.getCachedMeasurement(dataToMeasure) !== undefined
    ) {
      return false;
    }

    return true;
  }

  function getInitialResizeGroupState(
    props: AppResizeGroupProps
  ): AppResizeGroupState {
    return {
      dataToMeasure: { ...props.data },
      prevProps: props,
      resizeDirection: "grow",
      measureContainer: true,
    };
  }

  return {
    getNextState,
    shouldRenderDataForMeasurement,
    getInitialResizeGroupState,
  };
};

// Provides a context property that (if true) tells any child components that
// they are only being used for measurement purposes and will not be visible.
export const MeasuredContext = React.createContext({ isMeasured: false });
MeasuredContext.displayName = "MeasuredContext";

// Styles for the hidden div used for measurement
// We use position: absolute to put the hidden div on top of the visible div to ensure
// switching to the new rendered data when measurement is completed will avoid jarring effects
const hiddenDivStyles: React.CSSProperties = {
  position: "absolute",
  visibility: "hidden",
};
const hiddenParentStyles: React.CSSProperties = { position: "relative" };

export class AppResizeGroup extends BaseComponent<
  AppResizeGroupProps,
  AppResizeGroupState
> {
  private _nextResizeGroupStateProvider = getNextResizeGroupStateProvider();
  // The root div which is the container inside of which we are trying to fit content.
  private _root = React.createRef<HTMLDivElement>();
  // A div that can be used for the initial measurement so that we can avoid mounting a second instance
  // of the component being measured for the initial render.
  private _initialHiddenDiv = React.createRef<HTMLDivElement>();
  // A hidden div that is used for mounting a new instance of the component for measurement in a hidden
  // div without unmounting the currently visible content.
  private _updateHiddenDiv = React.createRef<HTMLDivElement>();

  public static getDerivedStateFromProps(
    props: AppResizeGroupProps,
    state: AppResizeGroupState
  ) {
    if (state.prevProps !== props) {
      const { data: prevData } = state.prevProps!; // We set initial prevProps in getInitialState.
      const { data, onBatchScaleData } = props;
      const { renderedData } = state;

      // If the data needs to remeasure again we do not perform this optimization at all.
      // If cache key is defined and equal to cache key of previous props' data we can do an optimization
      if (
        !props.dataNeedsMeasuring &&
        data &&
        prevData &&
        prevData.cacheKey &&
        data.cacheKey === prevData.cacheKey
      ) {
        // If they have batch scaling ability, and data has scaling steps count.
        if (
          onBatchScaleData &&
          data.appliedScalingStepCount !== undefined &&
          renderedData &&
          renderedData.appliedScalingStepCount !== undefined
        ) {
          const newData = onBatchScaleData(
            { ...data },
            renderedData.appliedScalingStepCount
          );
          // If new scaled data has same cacheKey as previously rendered data, it can safely be re-rendered
          if (newData && newData.cacheKey === renderedData.cacheKey) {
            return {
              renderedData: newData,
              measureContainer: true, // Receiving new props means the parent might re-render and the root width/height might change
              prevProps: props,
            };
          }
        }
      }

      return {
        dataToMeasure: { ...data },
        prevProps: props,
        resizeDirection: "grow",
        measureContainer: true, // Receiving new props means the parent might re-render and the root width/height might change
      };
    }

    return null;
  }

  constructor(props: AppResizeGroupProps) {
    super(props);
    this.state = this._nextResizeGroupStateProvider.getInitialResizeGroupState(
      this.props
    );

    this._warnDeprecations({
      styles: "className",
    });
  }

  /**
   * Normal shallow comparison of props and state except for one optimization.
   * When the only change in both props and state is that the measureContainer part of the state
   * changed from true to false, we do not need to re-render.
   */
  public shouldComponentUpdate(
    nextProps: AppResizeGroupProps,
    nextState: AppResizeGroupState
  ) {
    const propsCompare = shallowCompare(this.props, nextProps);
    if (nextState.measureContainer === false) {
      // If there is a state change just because of measureContainer going from true -> false and everything else
      // is the same as before, we do not need to re-render
      if (
        propsCompare &&
        shallowCompare({ ...this.state, measureContainer: false }, nextState)
      ) {
        return false;
      }
    }
    return !propsCompare || !shallowCompare(this.state, nextState);
  }

  public render(): JSX.Element {
    const { className, onRenderData, measureContentVisibly } = this.props;
    const { dataToMeasure, renderedData } = this.state;
    const divProps = getNativeProps<React.HTMLAttributes<HTMLDivElement>>(
      this.props,
      divProperties,
      ["data"]
    );

    const dataNeedsMeasuring =
      this._nextResizeGroupStateProvider.shouldRenderDataForMeasurement(
        dataToMeasure
      ) ||
      (this.props.dataNeedsMeasuring && dataToMeasure);

    const isInitialMeasure = !this.hasRenderedContent() && dataNeedsMeasuring;

    // We only ever render the final content to the user. All measurements are done in a hidden div.
    // For the initial render, we want this to be as fast as possible, so we need to make sure that we only mount one version of the
    // component for measurement and the final render. For renders that update what is on screen, we want to make sure that
    // there are no jarring effects such as the screen flashing as we apply scaling steps for measurement. In the update case,
    // we mount a second version of the component just for measurement purposes and leave the rendered content untouched until we know the
    // next state sto show to the user.
    return (
      <div {...divProps} className={className} ref={this._root}>
        <div style={!measureContentVisibly ? hiddenParentStyles : undefined}>
          {dataNeedsMeasuring && !isInitialMeasure && (
            <div
              style={!measureContentVisibly ? hiddenDivStyles : undefined}
              ref={this._updateHiddenDiv}
            >
              <MeasuredContext.Provider value={{ isMeasured: true }}>
                {onRenderData(dataToMeasure)}
              </MeasuredContext.Provider>
            </div>
          )}

          <div
            ref={this._initialHiddenDiv}
            style={
              isInitialMeasure && !measureContentVisibly
                ? hiddenDivStyles
                : undefined
            }
            data-unique-id="visibleContent"
          >
            <MeasuredContext.Provider
              value={getMemoizedMeasuredContext(!this.hasRenderedContent())}
            >
              {isInitialMeasure
                ? onRenderData(dataToMeasure)
                : renderedData && onRenderData(renderedData)}
            </MeasuredContext.Provider>
          </div>
        </div>
      </div>
    );
  }

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  // Local members for handling and disposing of the resize handler
  private resizeHandler: ((args?: any) => void) | undefined = undefined;
  private events: EventGroup | undefined = undefined;
  private async: Async | undefined = undefined;
  private resizeWindow: Window | undefined = undefined;

  public componentDidMount(): void {
    this.resizeWindow = this.context.appWindow ?? window;
    this.events = new EventGroup(this.resizeWindow);
    this.async = new Async(this.resizeWindow);
    this.resizeHandler = this.async.debounce(this.onResize, RESIZE_DELAY, {
      leading: true,
    });
    this.events.on(this.resizeWindow, "resize", this.resizeHandler);

    this.afterComponentRendered(this.props.direction);
  }

  public componentWillUnmount(): void {
    this.events?.off(this.resizeWindow, "resize", this.resizeHandler);
    this.resizeHandler = undefined;
    this.resizeWindow = undefined;
    this.async?.dispose();
    this.events?.dispose();
  }

  public componentDidUpdate() {
    if (this.state.renderedData) {
      if (this.props.dataDidRender) {
        this.props.dataDidRender(this.state.renderedData);
      }
    }
    this.afterComponentRendered(this.props.direction);
  }

  public remeasure(): void {
    if (this._root.current) {
      this.setState({ measureContainer: true });
    }
  }

  private afterComponentRendered(direction?: AppResizeGroupDirection): void {
    this.async?.requestAnimationFrame(() => {
      let containerDimension = undefined;
      if (this.state.measureContainer && this._root.current) {
        const boundingRect = this._root.current.getBoundingClientRect();
        containerDimension =
          direction && direction === AppResizeGroupDirection.vertical
            ? boundingRect.height
            : boundingRect.width;
      }
      const nextState = this._nextResizeGroupStateProvider.getNextState(
        this.props,
        this.state,
        this.props.measureRenderedContent
          ? this.props.measureRenderedContent
          : () => {
              const refToMeasure = !this.hasRenderedContent()
                ? this._initialHiddenDiv
                : this._updateHiddenDiv;
              if (!refToMeasure.current) {
                return 0;
              }
              return direction && direction === AppResizeGroupDirection.vertical
                ? refToMeasure.current.scrollHeight
                : refToMeasure.current.scrollWidth;
            },
        containerDimension
      );

      // When the content has been rendered visibly, we don't need to re-render into a visible div
      if (nextState && !this.props.measureContentVisibly) {
        this.setState(nextState);
      } else {
        this.props.onAfterScalingComplete?.(this.state.renderedData);
      }
    }, this._root.current);
  }

  private onResize = (): void => {
    if (this._root.current) {
      this.setState({ measureContainer: true });
    }
  };

  /**
   * Method to determine if we have rendered content in this ResizeGroup before.
   * If we did, we should have a renderedData set in the state,
   */
  private hasRenderedContent(): boolean {
    return !!this.state.renderedData;
  }
}

const getMemoizedMeasuredContext = memoizeFunction((isMeasured: boolean) => {
  return { isMeasured };
});
